// Third-party
import React from 'react';
import { Waypoint } from 'react-waypoint';
import classNames from 'classnames';
import { RowInfo, SortingRule } from 'react-table';

// Components
import { LoadingSpinner } from '../../components/common';
import { PortfoliosTable } from '../../components/tables';
import { PrimaryButton } from '../../components/buttons';
import { SearchInput } from '../../components/inputs';
import { PortfolioLinePerformance } from '../../components/misc';
import { OwnProps as PortfolioLinePerformanceProps } from '../../components/misc/PortfolioLinePerformance';
import { Typography } from '@capintel/component-lib/typography';

// Utils
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { benchmarkToComparisonItem } from '../../../utils/comparison';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../utils/permissions';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '../../../enums/Portfolio';
import User from '@/types/user';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';

type Props = {
  user: User;
  query?: string;
  portfoliosTableRef?: React.RefObject<PortfoliosTable<BenchmarkPortfolio>>;
  searching: boolean;
  initialized: boolean;
  benchmarkPortfolios: BenchmarkPortfolio[];
  hasMore: boolean;
  onLoadMore: () => void;
  onSort: (sorting: SortingRule[]) => void;
  onSearch: (query: string) => void;
  onDeletePortfolio: (benchmarkPortfolios: BenchmarkPortfolio[]) => void;
  onEditPortfolio: (benchmarkPortfolio: BenchmarkPortfolio) => void;
  onCompare: (portfobenchmarkPortfoliolio: BenchmarkPortfolio) => void;
  onCompareSelected: (benchmarkPortfolios: BenchmarkPortfolio[]) => void;
  onCreatePortfolio: () => void;
  comparingBenchmarkPortfolioIds: string[];
};

class LegacyBenchmarkPortfolios extends React.Component<Props> {
  containerRef = React.createRef<HTMLDivElement>();

  getDisplayedColumns = () => {
    const { user } = this.props;
    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  renderPortfoliosSection = (title: string, benchmarkPortfolios: BenchmarkPortfolio[]) => {
    const { hasMore, searching, initialized } = this.props;
    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE, PORTFOLIO_METRICS_FIELDS.YEAR_FIVE];

    return (
      <div className="portfolios-section">
        <div className="section-headers">
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
            {title}
          </Typography>
          <div className="section-actions">
            <SearchInput loading={searching} key="models" placeholder={formatMessage('Models.SearchAllBenchmarkPortfolios')} onChange={this.props.onSearch} />
            <PrimaryButton onClick={this.props.onCreatePortfolio}>
              <i className="icon-plus" />
              <FormattedMessage id="Benchmarks.CreateCustomBenchmark" />
            </PrimaryButton>
          </div>
        </div>

        <div className="benchmark-portfolios">
          {(searching || !initialized) && <LoadingSpinner />}

          <PortfoliosTable<BenchmarkPortfolio>
            ref={this.props.portfoliosTableRef}
            portfolios={benchmarkPortfolios}
            className={classNames({ 'visually-hidden': searching || !initialized })}
            displayedColumns={displayedColumns}
            displayedMetrics={displayedMetrics}
            onSort={this.props.onSort}
            onLoadMore={this.props.onLoadMore}
            onDelete={this.props.onDeletePortfolio}
            onEdit={this.props.onEditPortfolio}
            onCompare={this.props.onCompareSelected}
            selectedTitle="PortfoliosTable.SelectedTitle.Benchmarks"
            comparingPortfolioIds={this.props.comparingBenchmarkPortfolioIds}
            messageIds={{
              deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedBenchmarks',
              deleteSingle: 'PortfoliosTable.Buttons.DeleteBenchmark',
              editSingle: 'PortfoliosTable.Buttons.EditBenchmark',
            }}
            performanceSubcomponent={(row: RowInfo) => {
              const subComponentProps: PortfolioLinePerformanceProps<BenchmarkPortfolio> = {
                portfolio: row.original,
                transformPortfolioToComparisonItem: () => benchmarkToComparisonItem(row.original),
                displayedMetrics,
                endpointEntity: 'benchmark-portfolios',
              };
              return <PortfolioLinePerformance {...subComponentProps} />;
            }}
          />

          {hasMore && initialized && !searching && (
            <>
              <Waypoint onEnter={this.props.onLoadMore} />
              <LoadingSpinner />
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { benchmarkPortfolios } = this.props;

    return (
      <div ref={this.containerRef} className="all-models">
        {this.renderPortfoliosSection(formatMessage('Models.MyCustomBenchmarks'), benchmarkPortfolios)}
      </div>
    );
  }
}

export default LegacyBenchmarkPortfolios;
